import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState, useRef } from "react";
import { Text, Modal, View, Title } from "@constants/Themed";
import { FlatList, Platform, Pressable } from 'react-native';
import { useQuery } from "@tanstack/react-query";
import Checkbox from "expo-checkbox";
import AnimatedLottieView from "@utils/lottieAnimation";
import Button from "@components/UI/Button/Button";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import styles from "@stylesheet";
import RenderCalendarList from "./calendarListItem";
import getLocalCalendars from "../utils/getLocalCalendar";
import { AntDesign } from '@expo/vector-icons';
import apis from "@api/api";
import DeviceInfo from "react-native-device-info";
import { H5, Label, XStack } from "tamagui";
import FlipCard from "react-native-flip-card";
import GenerateSchemaForm from "@hooks/generateSchemaForm";
import { schemaCache } from '@utils/schemaCache';


type CalendarSelectionModalProps = {
  calendarSelectionCallback: (value: string[]) => void;
  calendars: [Object];
};

export type CalendarSelectionModalHandle = {
  openModal: (value) => void;
};

const CalendarSelectionModal = forwardRef<CalendarSelectionModalHandle, CalendarSelectionModalProps>((props, ref) => {

  useImperativeHandle(ref, () => ({
    openModal: (value) => {
      console.log(value);
      setVisible(true);
    }
  }));

  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState([]);
  const [calendars, setCalendars] = useState(props.calendars);
  const [flip, setFlip] = useState(false);
  const theme = themeSelector();
  const addCalRef = useRef(null);
  const deviceInfo = DeviceInfo.getDeviceId();

  // Use cached schema
  const schema = schemaCache.getSchema('wholeCalendarSchema'); 

  useEffect(() => {
    if (!calendars?.length && Platform.OS != 'web') {
      const getCalendars = async () => {
        await getLocalCalendars().then(async (res) => {
          //Synchronise selected calendars with db, both user model and calendar models
          setCalendars(res);
        })
      }
      getCalendars();
    }
  }, [calendars])

  const handleCallback = async () => {
    if (props.calendarSelectionCallback && selected?.length > 0) {
      setVisible(false);
      await apis.syncCalendars(selected, deviceInfo).then((res) => {
        props.calendarSelectionCallback(selected);
        setVisible(false);
      }).catch(() => {
        setVisible(true);
      })
    }
  }


  const handleSelection = useCallback((value) => {
    if (selected.includes(value)) {
      const sele = selected.filter((item) => item !== value)
      setSelected((sele));
    }
    else {
      if (selected?.length >= 3) {
        return;
      }
      setSelected((prev) => [...prev, value]);
    }
  }, [selected]);


  return (
    <Modal
      setVisible={setVisible}
      extraChildren={(<XStack gap={20} style={{ paddingHorizontal: 20, flexDirection: 'row', flex: 1, minWidth: "100%" }}>
        <Button onPress={() => setFlip(!flip)} customStyle={{ flex: 1 }} buttonText={flip ? `Import calendar` : `Create new calendar`} />
        {
          !flip ?
            <Button customStyle={{ flex: 1 }} disabled={selected?.length > 0 ? false : true} defaultColor={selected?.length > 0 ? undefined : 'grey'} onPress={handleCallback} buttonText={`Select(${selected?.length})`} />
            :
            <Button customStyle={{ flex: 1 }} onPress={() => addCalRef.current?.submitForm?.()} buttonText={`Create calendar`} />
        }
      </XStack>)}
      isVisible={visible} style={{ marginHorizontal: 20, marginTop: 50, overflow: "visible", paddingVertical: 20 }} onBackdropPress={() => setVisible(false)} >
      {/* <Pressable onPress={() => setVisible(false)} style={{ zIndex: 10, position: 'absolute', top: -15, right: -15, borderRadius: 40 }}>
        <AntDesign name="close-circle" size={40} color="red" />
      </Pressable> */}
      <FlipCard
        clickable={false}
        flip={flip}
        flipVertical={false}
        flipHorizontal={true}
        style={{ flex: 6 }}
      >
        <View style={[styles.face, { padding: 20, borderRadius: 20, flex: 6 }]}>
          <View style={{ flexDirection: 'row' }}>
            <Title style={{ flex: 3 }}>Import local calendars</Title>
            <AnimatedLottieView style={{ flex: 1 }} source={require('@assets/animations/101700-calendar-animation.json')} />
          </View>
          <Text style={{ fontStyle: 'italic' }}>Import calendars from your device to stay in sync across your projects.</Text>
          <View style={{ flex: 10 }}>
            <FlatList contentContainerStyle={{ gap: 10 }} style={{ marginVertical: 20 }} data={calendars}
              renderItem={({ item, index }) => <RenderCalendarList alreadyImported={calendars?.filter((cal) => cal?.title === item?.title).length > 0} listLength={selected?.length} handleSelection={(item) => handleSelection(item)} item={item} index={index} />}
            />
          </View>
        </View>
        <View style={[styles.back, {
          flex: 1,
          padding: 15,
          paddingVertical: 20
        }]}>
          <H5 textAlign="center">Add a new calendar</H5>
          <Label style={{ fontSize: 15, textAlign: "center", lineHeight: 25, marginVertical: 10 }}>New calendars with SmartSaaS can help you organise bookings and scheduling with any products you have made.</Label>
          <GenerateSchemaForm
            submitFunction={async (v: any) => await apis.addSSCal({ ...v, type: "smartsaas" })}
            hideButton
            customStyling={{ marginBottom: 40}}
            ref={addCalRef}
            schemaName="calendar"
            nullify={['availability', 'companyId', 'billingWarning', 'reminders', 'timezone', 'type', 'calendar', 'device', 'userId', 'calendarId']}
            schema={schema} />
        </View>
      </FlipCard>

    </Modal>
  )
});

export default CalendarSelectionModal;
